import { type KeyboardEvent, useEffect, useRef } from "react";
import type { Market } from "../../../domain/location/types";
import { useT } from "../../../i18n";
import type { BuilderWorkspace } from "../types";
import type { WorkspaceBadges } from "../workspaceBadges";
import {
	getBaseWorkspaces,
	WORKSPACE_PANEL_ID,
	workspaceTabId,
} from "../workspaces";

interface BuilderWorkspaceNavProps {
	activeWorkspace: BuilderWorkspace;
	onSetActiveWorkspace: (workspace: BuilderWorkspace) => void;
	market: Market;
	badges?: WorkspaceBadges;
	/**
	 * Workspace whose badge just hit a milestone. Drives a brief, muted settle
	 * on that badge - the toast carries the words, this only points at where
	 * the change happened.
	 */
	celebrateWorkspace?: BuilderWorkspace | null;
}

export function BuilderWorkspaceNav({
	activeWorkspace,
	onSetActiveWorkspace,
	market,
	badges,
	celebrateWorkspace = null,
}: BuilderWorkspaceNavProps) {
	const t = useT();
	const workspaces = getBaseWorkspaces(market);
	const tabRefs = useRef(new Map<BuilderWorkspace, HTMLButtonElement>());

	// On a phone the strip scrolls sideways; keep the active tab in view so
	// Budget, Build guide and Safety are never selected off-screen.
	useEffect(() => {
		tabRefs.current
			.get(activeWorkspace)
			?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
	}, [activeWorkspace]);

	// WAI-ARIA tabs: one tab stop, arrows move between tabs and activate them.
	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		const index = workspaces.findIndex((item) => item.id === activeWorkspace);
		const last = workspaces.length - 1;
		const target =
			event.key === "ArrowRight"
				? index === last
					? 0
					: index + 1
				: event.key === "ArrowLeft"
					? index === 0
						? last
						: index - 1
					: event.key === "Home"
						? 0
						: event.key === "End"
							? last
							: null;
		if (target === null) return;
		event.preventDefault();
		const next = workspaces[target].id;
		onSetActiveWorkspace(next);
		tabRefs.current.get(next)?.focus();
	};

	return (
		<nav
			className="builder-workspace-nav"
			role="tablist"
			aria-label={t.workspace_navAriaLabel}
		>
			{workspaces.map((workspace) => {
				const isActive = workspace.id === activeWorkspace;
				const badge = badges?.[workspace.id];
				// Fold the badge value into the tab's accessible name so a screen
				// reader announces it in words once; the visible glyph is decorative
				// (aria-hidden) so the number isn't read twice.
				const accessibleName = badge
					? `${workspace.label}, ${badge.ariaText}`
					: undefined;

				return (
					<button
						key={workspace.id}
						ref={(node) => {
							if (node) tabRefs.current.set(workspace.id, node);
							else tabRefs.current.delete(workspace.id);
						}}
						id={workspaceTabId(workspace.id)}
						type="button"
						role="tab"
						className={`builder-workspace-tab${isActive ? " active" : ""}`}
						aria-selected={isActive}
						aria-controls={isActive ? WORKSPACE_PANEL_ID : undefined}
						aria-label={accessibleName}
						tabIndex={isActive ? 0 : -1}
						onClick={() => onSetActiveWorkspace(workspace.id)}
						onKeyDown={handleKeyDown}
					>
						<span className="builder-workspace-tab-label">
							{workspace.label}
						</span>
						{badge && (
							<span
								className={`builder-workspace-tab-badge builder-workspace-tab-badge--${badge.status}${
									celebrateWorkspace === workspace.id ? " is-celebrating" : ""
								}`}
								aria-hidden="true"
							>
								{badge.text}
							</span>
						)}
					</button>
				);
			})}
		</nav>
	);
}
